import * as React from "react";

import SvgXClose from "@uui/XClose";

import { styled, ListItem, SxProps, Theme } from "@mui/material";
import MuiAutocomplete, {
  AutocompleteProps as MuiAutocompleteProps,
} from "@mui/material/Autocomplete";
import Chip, { ChipProps, ChipTypeMap } from "@mui/material/Chip";

import { lightOrDark } from "@app/embedded/theme/utils";

interface TagAutocompleteProps<
  T,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = ChipTypeMap["defaultComponent"],
> extends MuiAutocompleteProps<
    T,
    true,
    DisableClearable,
    FreeSolo,
    ChipComponent
  > {
  name: string;
  ChipComponent?: React.FC<ChipProps>;
  OptionComponent?: typeof ListItem;
  searchOnType?: boolean;
  getTagLabel?: (v: T) => string;
  getTagSxProps?: (v: T, index: number) => SxProps<Theme>;
}

const StyledChip = styled((props: ChipProps) => <Chip {...props} />)(
  ({ theme }) => ({
    backgroundColor: lightOrDark(
      theme.palette.shades.ghost[500],
      theme.palette.shades.secondary[600]
    )(theme),
    fontFamily: theme.typography.fontFamily,
    color: lightOrDark(
      theme.palette.shades.neutral[800],
      theme.palette.shades.neutral[50]
    )(theme),
    fontSize: "16px",
    ".MuiChip-deleteIcon": {
      height: "14px",
      color: lightOrDark(
        theme.palette.shades.neutral[500],
        theme.palette.shades.neutral[100]
      )(theme),
    },
  })
);

const TagAutocomplete = <
  T,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  name,
  ChipComponent = StyledChip,
  OptionComponent = ListItem,
  searchOnType = false,
  getTagSxProps = () => ({}),
  getTagLabel = (v) => (v ? v.toString() : ""),
  getOptionLabel = (v) => (v ? v.toString() : ""),
  ...rest
}: TagAutocompleteProps<T, DisableClearable, FreeSolo>) => {
  const props = { getOptionLabel, ...rest };

  return (
    <MuiAutocomplete
      id={`${name}-autocomplete`}
      multiple
      autoComplete
      includeInputInList
      filterSelectedOptions
      renderTags={
        props.renderTags
          ? props.renderTags
          : (value, getTagProps) =>
              value.map((v, index: number) => (
                <ChipComponent
                  {...getTagProps({ index })}
                  sx={{ ...getTagSxProps(v, index) }}
                  label={getTagLabel(v)}
                  deleteIcon={<SvgXClose />}
                  key={`${getTagLabel(v)}-${index}`}
                />
              ))
      }
      filterOptions={searchOnType ? (x) => x : undefined}
      renderOption={
        props.renderOption
          ? props.renderOption
          : (props, option) => {
              return (
                <OptionComponent {...props} key={props.id}>
                  {getOptionLabel(option)}
                </OptionComponent>
              );
            }
      }
      {...props}
    />
  );
};
export default TagAutocomplete;
